import { supabase } from '../lib/supabase';

/**
 * Assignment Service
 * Handles paper-to-reviewer assignment operations
 * Converts between snake_case (database) and camelCase (React)
 */

export const assignmentService = {
  /**
   * Get all reviewers for a conference with workload and expertise
   */
  async getReviewersForConference(conferenceId) {
    try {
      // Get all reviewers for the conference
      const { data: reviewerRoles, error: rolesError } = await supabase
        ?.from('conference_roles')
        ?.select(`
          user_id,
          user_profiles (
            id, first_name, last_name, email, institution,
            subject_areas, experience_level
          )
        `)
        ?.eq('conference_id', conferenceId)
        ?.eq('role', 'reviewer');

      if (rolesError) throw rolesError;

      // Get current workload (count of assigned reviews)
      const { data: workloadData, error: workloadError } = await supabase
        ?.from('submission_reviews')
        ?.select('reviewer_id')
        ?.eq('conference_id', conferenceId);

      if (workloadError) throw workloadError;

      // Calculate workload per reviewer
      const workloadMap = {};
      workloadData?.forEach(review => {
        workloadMap[review?.reviewer_id] = (workloadMap?.[review?.reviewer_id] || 0) + 1;
      });

      // Map reviewers with workload
      return reviewerRoles?.map(role => {
        const profile = role?.user_profiles;
        return {
          id: profile?.id,
          firstName: profile?.first_name,
          lastName: profile?.last_name,
          email: profile?.email,
          institution: profile?.institution,
          subjectAreas: profile?.subject_areas || [],
          experienceLevel: profile?.experience_level,
          currentWorkload: workloadMap?.[profile?.id] || 0
        };
      }) || [];
    } catch (error) {
      console.error('Get reviewers error:', error);
      throw error;
    }
  },

  /**
   * Get unassigned papers for a conference
   */
  async getUnassignedPapers(conferenceId) {
    try {
      // Get all submitted papers
      const { data: submissions, error: submissionsError } = await supabase
        ?.from('conference_submissions')
        ?.select(`
          *,
          user_profiles (first_name, last_name, email, institution),
          conference_tracks (name)
        `)
        ?.eq('conference_id', conferenceId)
        ?.eq('status', 'submitted');

      if (submissionsError) throw submissionsError;

      // Get current assignments
      const { data: assignments, error: assignmentsError } = await supabase
        ?.from('submission_reviews')
        ?.select('submission_id, reviewer_id')
        ?.eq('conference_id', conferenceId);

      if (assignmentsError) throw assignmentsError;

      // Get conference min_reviews
      const { data: conference, error: confError } = await supabase
        ?.from('conferences')
        ?.select('min_reviews')
        ?.eq('id', conferenceId)
        ?.single();

      if (confError) throw confError;

      const minReviews = conference?.min_reviews || 3;

      // Count assignments per paper
      const assignmentMap = {};
      assignments?.forEach(assignment => {
        if (!assignmentMap?.[assignment?.submission_id]) {
          assignmentMap[assignment?.submission_id] = [];
        }
        assignmentMap?.[assignment?.submission_id]?.push(assignment?.reviewer_id);
      });

      // Map papers with assignment info
      return submissions?.map(sub => {
        const assignedReviewers = assignmentMap?.[sub?.id] || [];
        return {
          id: sub?.id,
          title: sub?.title,
          abstract: sub?.abstract,
          keywords: sub?.keywords || [],
          trackId: sub?.track_id,
          trackName: sub?.conference_tracks?.name,
          authorId: sub?.user_id,
          authorName: `${sub?.user_profiles?.first_name} ${sub?.user_profiles?.last_name}`,
          authorInstitution: sub?.user_profiles?.institution,
          submittedAt: sub?.submitted_at,
          assignedReviewers: assignedReviewers,
          requiredReviewers: minReviews,
          isFullyAssigned: assignedReviewers?.length >= minReviews
        };
      }) || [];
    } catch (error) {
      console.error('Get unassigned papers error:', error);
      throw error;
    }
  },

  /**
   * Calculate expertise match score between paper and reviewer
   */
  calculateExpertiseMatch(paperKeywords, reviewerSubjectAreas) {
    if (!paperKeywords?.length || !reviewerSubjectAreas?.length) return 0;

    const paperKeywordsLower = paperKeywords?.map(k => k?.toLowerCase());
    const reviewerAreasLower = reviewerSubjectAreas?.map(a => a?.toLowerCase());

    let matchCount = 0;
    paperKeywordsLower?.forEach(keyword => {
      if (reviewerAreasLower?.some(area => 
        area?.includes(keyword) || keyword?.includes(area)
      )) {
        matchCount++;
      }
    });

    return Math.round((matchCount / paperKeywords?.length) * 100);
  },

  /**
   * Detect conflicts of interest
   */
  detectConflicts(paper, reviewer) {
    const conflicts = [];

    // Same institution conflict
    if (paper?.authorInstitution && reviewer?.institution &&
        paper?.authorInstitution?.toLowerCase() === reviewer?.institution?.toLowerCase()) {
      conflicts?.push({
        type: 'institution',
        message: 'Same institution as author'
      });
    }

    // Author is reviewer conflict
    if (paper?.authorId === reviewer?.id) {
      conflicts?.push({
        type: 'self',
        message: 'Author cannot review own paper'
      });
    }

    return conflicts;
  },

  /**
   * Assign reviewer to paper
   */
  async assignReviewer(conferenceId, submissionId, reviewerId) {
    try {
      // 🔍 DIAGNOSTIC: Check current user's role in this conference
      const { data: { user } } = await supabase?.auth?.getUser();
      console.log('🔍 Current user attempting assignment:', user?.id);
      
      const { data: userRole, error: roleError } = await supabase
        ?.from('conference_roles')
        ?.select('role')
        ?.eq('conference_id', conferenceId)
        ?.eq('user_id', user?.id)
        ?.maybeSingle();
      
      console.log('👤 User role in conference:', userRole?.role || 'NO ROLE FOUND');
      
      if (roleError) {
        console.error('❌ Error checking user role:', roleError);
      }
      
      if (!userRole || !['chair', 'organizer']?.includes(userRole?.role)) {
        throw new Error(`You must be a conference chair or organizer to assign reviewers. Current role: ${userRole?.role || 'none'}`);
      }

      // Check if assignment already exists
      const { data: existing, error: checkError } = await supabase
        ?.from('submission_reviews')
        ?.select('id')
        ?.eq('submission_id', submissionId)
        ?.eq('reviewer_id', reviewerId)
        ?.maybeSingle();

      if (checkError) throw checkError;

      if (existing) {
        throw new Error('Reviewer already assigned to this paper');
      }

      // Create assignment
      console.log('📝 Attempting to insert review assignment:', { submission_id: submissionId, reviewer_id: reviewerId, conference_id: conferenceId });
      
      const { data, error } = await supabase
        ?.from('submission_reviews')
        ?.insert({
          submission_id: submissionId,
          reviewer_id: reviewerId,
          conference_id: conferenceId,
          is_draft: true
        })
        ?.select()
        ?.single();

      if (error) {
        console.error('❌ Error inserting review assignment:', error);
        throw error;
      }
      
      console.log('✅ Review assignment created successfully:', data?.id);

      // Ensure the reviewer has a conference_roles entry — required by RLS on conference_submissions.
      // Normally this is set when they accept the invitation, but insert defensively.
      await supabase
        ?.from('conference_roles')
        ?.upsert(
          { conference_id: conferenceId, user_id: reviewerId, role: 'reviewer' },
          { onConflict: 'conference_id,user_id,role', ignoreDuplicates: true }
        );

      // Send email notification to reviewer
      try {
        // Get reviewer details
        const { data: reviewer, error: reviewerError } = await supabase
          ?.from('user_profiles')
          ?.select('email, first_name, last_name')
          ?.eq('id', reviewerId)
          ?.single();

        if (reviewerError) throw reviewerError;

        // Get paper details
        const { data: submission, error: submissionError } = await supabase
          ?.from('conference_submissions')
          ?.select('title')
          ?.eq('id', submissionId)
          ?.single();

        if (submissionError) throw submissionError;

        // Get conference details
        const { data: conference, error: conferenceError } = await supabase
          ?.from('conferences')
          ?.select('name')
          ?.eq('id', conferenceId)
          ?.single();

        if (conferenceError) throw conferenceError;

        // Import notification service dynamically to avoid circular dependency
        const { notificationService } = await import('./notificationService.js');

        // Send email
        await notificationService?.sendReviewerAssignmentEmail(
          reviewer?.email,
          `${reviewer?.first_name} ${reviewer?.last_name}`,
          submission?.title,
          conference?.name,
          conferenceId
        );
      } catch (emailError) {
        console.error('Failed to send assignment email:', emailError);
        // Don't fail the assignment if email fails
      }

      return {
        id: data?.id,
        submissionId: data?.submission_id,
        reviewerId: data?.reviewer_id,
        conferenceId: data?.conference_id
      };
    } catch (error) {
      console.error('Assign reviewer error:', error);
      throw error;
    }
  },

  /**
   * Remove reviewer assignment
   */
  async removeAssignment(submissionId, reviewerId) {
    try {
      const { error } = await supabase
        ?.from('submission_reviews')
        ?.delete()
        ?.eq('submission_id', submissionId)
        ?.eq('reviewer_id', reviewerId)
        ?.eq('is_draft', true);

      if (error) throw error;
    } catch (error) {
      console.error('Remove assignment error:', error);
      throw error;
    }
  },

  /**
   * Get assignment statistics
   */
  async getAssignmentStats(conferenceId) {
    try {
      const [papers, assignments, conference] = await Promise.all([
        supabase
          ?.from('conference_submissions')
          ?.select('id')
          ?.eq('conference_id', conferenceId)
          ?.eq('status', 'submitted'),
        supabase
          ?.from('submission_reviews')
          ?.select('submission_id, reviewer_id')
          ?.eq('conference_id', conferenceId),
        supabase
          ?.from('conferences')
          ?.select('min_reviews')
          ?.eq('id', conferenceId)
          ?.single()
      ]);

      const totalPapers = papers?.data?.length || 0;
      const minReviews = conference?.data?.min_reviews || 3;

      // Count assignments per paper
      const assignmentMap = {};
      assignments?.data?.forEach(assignment => {
        assignmentMap[assignment?.submission_id] = 
          (assignmentMap?.[assignment?.submission_id] || 0) + 1;
      });

      const fullyAssigned = Object.values(assignmentMap)?.filter(
        count => count >= minReviews
      )?.length || 0;

      const partiallyAssigned = Object.values(assignmentMap)?.filter(
        count => count > 0 && count < minReviews
      )?.length || 0;

      const unassigned = totalPapers - fullyAssigned - partiallyAssigned;

      return {
        totalPapers,
        fullyAssigned,
        partiallyAssigned,
        unassigned,
        totalAssignments: assignments?.data?.length || 0
      };
    } catch (error) {
      console.error('Get assignment stats error:', error);
      throw error;
    }
  },

  /**
   * Get workload balance recommendations
   */
  getWorkloadRecommendations(reviewers, targetWorkload) {
    const recommendations = [];

    reviewers?.forEach(reviewer => {
      const difference = reviewer?.currentWorkload - targetWorkload;
      
      if (difference > 2) {
        recommendations?.push({
          reviewerId: reviewer?.id,
          reviewerName: `${reviewer?.firstName} ${reviewer?.lastName}`,
          type: 'overloaded',
          message: `${difference} papers above target workload`,
          currentWorkload: reviewer?.currentWorkload,
          targetWorkload
        });
      } else if (difference < -2) {
        recommendations?.push({
          reviewerId: reviewer?.id,
          reviewerName: `${reviewer?.firstName} ${reviewer?.lastName}`,
          type: 'underutilized',
          message: `${Math.abs(difference)} papers below target workload`,
          currentWorkload: reviewer?.currentWorkload,
          targetWorkload
        });
      }
    });

    return recommendations;
  }
};

export default assignmentService;
